import React, { useState, useEffect } from "react";
import { useGetGraphicsItems } from "./hooks";
import { graphicsAPI, GraphicsItem } from "./api";
import { useI18n } from "../../i18n";

const GraphicsPage: React.FC = () => {
  const { t, code } = useI18n();
  const { data: items, isLoading, error } = useGetGraphicsItems();

  const isImage = (fileKey: string) => {
    if (!fileKey) return false;
    const imageExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".svg",
      ".webp",
      ".bmp",
    ];
    const lowercaseKey = fileKey.toLowerCase();
    return imageExtensions.some((ext) => lowercaseKey.includes(ext));
  };

  const ImagePreview: React.FC<{ item: GraphicsItem }> = ({ item }) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      if (item.fileKey && isImage(item.fileKey)) {
        setLoading(true);
        graphicsAPI
          .getPreviewUrl(item.id)
          .then((url) => {
            setPreviewUrl(url);
          })
          .catch((error) => {
            console.error("Failed to load preview:", error);
            setPreviewUrl(null);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }, [item.id, item.fileKey]);

    if (!item.fileKey || !isImage(item.fileKey)) {
      return (
        <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
          <div className="text-gray-400 text-center">
            <i className="fa-solid fa-image text-2xl mb-2 block"></i>
            <span className="text-xs">{t("graphics.noPreview")}</span>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 mx-auto mb-2"></div>
            <span className="text-xs text-gray-500">{t("common.loading.default")}</span>
          </div>
        </div>
      );
    }

    if (previewUrl) {
      return (
        <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
          <img
            src={previewUrl}
            alt={item.title}
            className="max-w-full max-h-full object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = `
                  <div class="text-gray-400 text-center">
                    <i class="fa-solid fa-image-broken text-2xl mb-2 block"></i>
                    <span class="text-xs">${t("graphics.previewUnavailable")}</span>
                  </div>
                `;
              }
            }}
          />
        </div>
      );
    }

    return (
      <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-gray-400 text-center">
          <i className="fa-solid fa-image text-2xl mb-2 block"></i>
          <span className="text-xs">{t("graphics.previewUnavailable")}</span>
        </div>
      </div>
    );
  };

  const handleDownload = async (id: string) => {
    try {
      await graphicsAPI.downloadFile(id);
    } catch (error) {
      alert(t("graphics.errors.downloadFailed"));
      console.error("Download error:", error);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "Logos & Brand Marks":
        return "fa-star text-purple-600";
      case "Marketing Banners":
        return "fa-rectangle-ad text-orange-600";
      case "Print Materials":
        return "fa-print text-teal-600";
      default:
        return "fa-image text-blue-600";
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col flex-1 h-full">
        <div className="bg-white px-8 pt-4 pb-2 border-b border-gray-200 flex flex-col gap-1 sticky top-0 z-20">
          <h1 className="text-2xl font-bold text-gray-900">{t("graphics.title")}</h1>
          <p className="text-sm text-gray-500 max-w-2xl">
            {t("graphics.subtitle")}
          </p>
        </div>
        <div className="flex-1 px-8 pb-8 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">{t("graphics.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col flex-1 h-full">
        <div className="bg-white px-8 pt-4 pb-2 border-b border-gray-200 flex flex-col gap-1 sticky top-0 z-20">
          <h1 className="text-2xl font-bold text-gray-900">{t("graphics.title")}</h1>
          <p className="text-sm text-gray-500 max-w-2xl">
            {t("graphics.subtitle")}
          </p>
        </div>
        <div className="flex-1 px-8 pb-8 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4 mx-auto">
              <i className="fa-solid fa-exclamation-triangle text-red-400 text-2xl"></i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("graphics.loadFailed")}
            </h3>
            <p className="text-gray-500 text-sm">
              {t("common.somethingWentWrong")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Group items by category
  const groupedItems =
    items?.reduce(
      (acc: { [key: string]: GraphicsItem[] }, item: GraphicsItem) => {
        if (!acc[item.category]) {
          acc[item.category] = [];
        }
        acc[item.category].push(item);
        return acc;
      },
      {}
    ) || {};

  return (
    <div className="flex flex-col flex-1 h-full">
      <div className="bg-white px-8 pt-4 pb-2 border-b border-gray-200 flex flex-col gap-1 sticky top-0 z-20">
        <h1 className="text-2xl font-bold text-gray-900">{t("graphics.title")}</h1>
        <p className="text-sm text-gray-500 max-w-2xl">
          {t("graphics.subtitle")}
        </p>
      </div>
      <div className="flex-1 px-8 pb-8">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          <p className="text-gray-700 mb-2">
            {t("graphics.intro")}
          </p>
          <p className="text-xs text-gray-400 mb-6">
            {t("common.lastUpdated", {
              date: new Date().toLocaleDateString(code),
            })}
          </p>

          {!items || items.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className="fa-solid fa-image text-gray-400 text-2xl"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t("graphics.empty.title")}
              </h3>
              <p className="text-gray-500 text-sm">
                {t("graphics.empty.body")}
              </p>
            </div>
          ) : (
            Object.entries(groupedItems).map(([category, categoryItems]) => (
              <div className="mb-8" key={category}>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <i
                    className={`fa-solid ${getCategoryIcon(category)} mr-2`}
                  ></i>
                  {category}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryItems.map((item) => (
                    <div
                      key={item.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50/50 transition-colors"
                    >
                      <ImagePreview item={item} />
                      <div className="mt-4 flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-800">
                            {item.title}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {item.description}
                          </p>
                        </div>
                        {item.fileKey ? (
                          <button
                            onClick={() => handleDownload(item.id)}
                            className="px-3 py-1.5 bg-[#111827] text-white rounded-lg hover:bg-gray-800 transition-colors text-xs font-medium flex items-center"
                          >
                            <i className="fa-solid fa-download mr-1"></i>{" "}
                            {t("graphics.download")}
                          </button>
                        ) : (
                          <button
                            disabled
                            className="px-3 py-1.5 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed text-xs font-medium flex items-center"
                          >
                            <i className="fa-solid fa-download mr-1"></i>{" "}
                            {t("graphics.download")}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default GraphicsPage;
